import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Shield, Plus, Eye, EyeOff, Copy, Check, Trash2, 
  Search, Filter, RefreshCw, Users, Server, Key,
  Building2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminVPNManagement = () => {
  const [loading, setLoading] = useState(true);
  const [vpnUsers, setVpnUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPasswords, setShowPasswords] = useState({});
  const [copiedFields, setCopiedFields] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [stats, setStats] = useState({
    total_vpn_users: 0,
    total_capacity: 0,
    tenants_with_vpn: 0
  });

  const [formData, setFormData] = useState({
    tenant_id: '',
    ip_address: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [vpnRes, tenantsRes] = await Promise.all([
        axios.get(`${API_URL}/api/v2/superadmin/vpn/all`, { headers }),
        axios.get(`${API_URL}/api/v2/tenants?per_page=1000`, { headers })
      ]);

      if (vpnRes.data.success) {
        setVpnUsers(vpnRes.data.data.vpn_users);
        setStats(vpnRes.data.data.stats);
      }

      if (tenantsRes.data.success) {
        setTenants(tenantsRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Gagal memuat data VPN');
    } finally {
      setLoading(false);
    }
  };

  // Refresh VPN data tanpa loading state (untuk setelah delete/create)
  const refreshVPNData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const vpnRes = await axios.get(`${API_URL}/api/v2/superadmin/vpn/all`, { headers });
      
      if (vpnRes.data.success) {
        setVpnUsers(vpnRes.data.data.vpn_users);
        setStats(vpnRes.data.data.stats);
      }
    } catch (error) {
      console.error('Error refreshing VPN data:', error);
    }
  };

  const handleCreateVPNUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v2/superadmin/vpn/create`,
        formData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('VPN user berhasil dibuat');
        setShowAddModal(false);
        setFormData({ tenant_id: '', ip_address: '', notes: '' });
        fetchData();
      }
    } catch (error) {
      console.error('Error creating VPN user:', error);
      toast.error(error.response?.data?.detail || 'Gagal membuat VPN user');
    }
  };

  const handleDeleteVPNUser = async (vpnUserId, vpnUsername, tenantName) => {
    if (!window.confirm(`Hapus VPN user "${vpnUsername}" dari tenant ${tenantName}?`)) return;

    try {
      const token = localStorage.getItem('token');
      
      // Show loading toast
      toast.loading('Menghapus VPN user...', { id: 'delete-vpn' });
      
      // Use username for more reliable deletion (works across both collections)
      const response = await axios.delete(
        `${API_URL}/api/v2/superadmin/vpn/by-username/${encodeURIComponent(vpnUsername)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`VPN user ${vpnUsername} berhasil dihapus`, { id: 'delete-vpn' });
        
        // Optimistic UI update - remove from local state immediately
        setVpnUsers(prev => prev.filter(u => u.username !== vpnUsername));
        
        // Update stats
        setStats(prev => ({
          ...prev,
          total_vpn_users: Math.max(0, prev.total_vpn_users - 1)
        }));
        
        // Then refresh data to ensure consistency
        setTimeout(() => {
          refreshVPNData();
        }, 500);
      } else {
        toast.error(response.data.message || 'Gagal menghapus VPN user', { id: 'delete-vpn' });
      }
    } catch (error) {
      console.error('Error deleting VPN user:', error);
      const errorMsg = error.response?.data?.detail || 'Gagal menghapus VPN user';
      toast.error(errorMsg, { id: 'delete-vpn' });
      
      // Refresh data anyway to ensure UI is in sync
      refreshVPNData();
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedFields({ ...copiedFields, [field]: true });
    toast.success('Tersalin ke clipboard');
    setTimeout(() => {
      setCopiedFields({ ...copiedFields, [field]: false });
    }, 2000);
  };

  const togglePasswordVisibility = (userId) => {
    setShowPasswords({ ...showPasswords, [userId]: !showPasswords[userId] });
  };

  const filteredVPNUsers = vpnUsers.filter(vpn => {
    const matchesTenant = selectedTenant === 'all' || vpn.tenant_id === selectedTenant;
    const matchesSearch = 
      vpn.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vpn.tenant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vpn.notes && vpn.notes.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesTenant && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">VPN Management</h1>
          <p className="text-sm text-gray-600 mt-1 dark:text-slate-300">Kelola VPN users untuk semua tenant</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowAddModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" />
            Add VPN User
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-slate-300">Total VPN Users</p>
                <p className="text-3xl font-bold text-gray-900 mt-1 dark:text-slate-100">{stats.total_vpn_users}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg dark:bg-blue-900/40">
                <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-slate-300">Total Kapasitas</p>
                <p className="text-3xl font-bold text-gray-900 mt-1 dark:text-slate-100">{stats.total_capacity}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg dark:bg-green-900/40">
                <Server className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-slate-300">Tenants dengan VPN</p>
                <p className="text-3xl font-bold text-gray-900 mt-1 dark:text-slate-100">{stats.tenants_with_vpn}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg dark:bg-purple-900/40">
                <Building2 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Cari username atau tenant..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-64">
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-slate-600"
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
              >
                <option value="all">Semua Tenant</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.tenant_name} ({tenant.tenant_id})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* VPN Users List */}
      <Card>
        <CardHeader>
          <CardTitle>VPN Users ({filteredVPNUsers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-gray-500 dark:text-slate-400">Loading VPN users...</div>
          ) : filteredVPNUsers.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 dark:text-slate-400">Tidak ada VPN users</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredVPNUsers.map((vpn) => (
                <div key={vpn.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 dark:border-slate-700 dark:hover:bg-slate-700/50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      {/* Tenant Info */}
                      <div>
                        <label className="text-xs text-gray-500 font-medium dark:text-slate-400">Tenant</label>
                        <div className="mt-1">
                          <p className="font-semibold text-gray-900 dark:text-slate-100">{vpn.tenant_name}</p>
                          <p className="text-xs text-gray-500 dark:text-slate-400">{vpn.tenant_id}</p>
                        </div>
                      </div>

                      {/* Username */}
                      <div>
                        <label className="text-xs text-gray-500 font-medium dark:text-slate-400">Username</label>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono dark:bg-slate-800">
                            {vpn.username}
                          </code>
                          <button
                            onClick={() => copyToClipboard(vpn.username, `user_${vpn.id}`)}
                            className="text-gray-500 hover:text-gray-700 dark:text-slate-400"
                          >
                            {copiedFields[`user_${vpn.id}`] ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {/* Password */}
                      <div>
                        <label className="text-xs text-gray-500 font-medium dark:text-slate-400">Password</label>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono dark:bg-slate-800">
                            {showPasswords[vpn.id] ? vpn.password : '••••••••••••'}
                          </code>
                          <button
                            onClick={() => togglePasswordVisibility(vpn.id)}
                            className="text-gray-500 hover:text-gray-700 dark:text-slate-400"
                          >
                            {showPasswords[vpn.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => copyToClipboard(vpn.password, `pass_${vpn.id}`)}
                            className="text-gray-500 hover:text-gray-700 dark:text-slate-400"
                          >
                            {copiedFields[`pass_${vpn.id}`] ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Action Button */}
                    <button
                      onClick={() => handleDeleteVPNUser(vpn.id, vpn.username, vpn.tenant_name)}
                      className="ml-4 p-2 text-red-500 hover:bg-red-50 rounded dark:hover:bg-red-900/30"
                      title="Delete VPN User"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Additional Info */}
                  {(vpn.ip_address || vpn.notes) && (
                    <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-4 text-sm">
                      {vpn.ip_address && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-slate-400">IP Address</label>
                          <p className="text-gray-900 dark:text-slate-100">{vpn.ip_address}</p>
                        </div>
                      )}
                      {vpn.notes && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-slate-400">Notes</label>
                          <p className="text-gray-900 dark:text-slate-100">{vpn.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add VPN User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full dark:bg-slate-800">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Add VPN User</h3>
            </div>
            <form onSubmit={handleCreateVPNUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Tenant *
                </label>
                <select
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-slate-600"
                  value={formData.tenant_id}
                  onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                >
                  <option value="">Pilih Tenant</option>
                  {tenants.map(tenant => {
                    const currentCount = vpnUsers.filter(v => v.tenant_id === tenant.id).length;
                    const maxCount = tenant.subscription_plan?.max_nas_vpn || 0;
                    const isFull = currentCount >= maxCount;
                    return (
                      <option 
                        key={tenant.id} 
                        value={tenant.id}
                        disabled={isFull}
                      >
                        {tenant.tenant_name} ({currentCount}/{maxCount}){isFull ? ' - FULL' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  IP Address (Optional)
                </label>
                <Input
                  type="text"
                  placeholder="10.10.10.1"
                  value={formData.ip_address}
                  onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                  Notes (Optional)
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:border-slate-600"
                  rows="3"
                  placeholder="Router Cabang A, Mikrotik RB750"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-900/30">
                <p className="text-xs text-blue-800">
                  <strong>Note:</strong> Username dan password akan di-generate otomatis.
                </p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddModal(false);
                    setFormData({ tenant_id: '', ip_address: '', notes: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                  Create VPN User
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminVPNManagement;
